import { useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    type DialogProps,
    DialogTitle,
    Typography,
    styled
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { unparse } from 'papaparse';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';
import * as yup from 'yup';

import { normalizeTranscriptRecord } from './utils';
import { ControlledDateRange } from '../../../../../components/forms';
import { StyledLoadingButton } from '../../../../../components/forms/LoadingButton';
import { Status } from '../../../../../types/apiTypes';
import DownloadTranscriptsContainer from '../../../DownloadTranscriptsContainer';
import { createAndClickDownloadUrl } from '../../../utils/CreateAndClickDownladUrl';
import { type TranscriptHistoricalRecord, type DownloadTranscriptOptions } from '../types';

const StyledDialog = styled(Dialog)`
    & .MuiDialog-paper {
        width: 500px;
    }

    & .MuiDialogTitle-root + .MuiDialogContent-root {
        padding-top: 16px;
    }
`;

const DateRangeContainer = styled(Box)`
    padding: 0 1.5rem 0.5rem 1.5rem;

    & > div {
        display: flex;
        align-items: center;
    }
`;

type DownloadTranscriptModalProps = DialogProps & {
    classId?: number;
    className?: string;
    userUuid: string;
    classUuid?: string;
};

export const DownloadTranscriptModal = (props: DownloadTranscriptModalProps) => {
    const { classId, className, userUuid, classUuid, onClose, ...other } = props;

    const { t } = useTranslation('admin_settings');

    const { getTranscripts, getTranscriptsStatus } = useContainer(DownloadTranscriptsContainer);

    const { enqueueSnackbar } = useSnackbar();

    const transcriptStatus = useMemo(() => getTranscriptsStatus[classId ?? 0], [classId, getTranscriptsStatus]);

    const schema = yup.object({
        downloadTranscriptDateRange: yup.string().required(t('errors.required-date-range'))
    });

    const { control, getValues, handleSubmit, setError, watch, clearErrors } = useForm({
        resolver: yupResolver(schema)
    });

    const watchedDateRange = watch('downloadTranscriptDateRange');

    useEffect(() => {
        if (watchedDateRange) {
            clearErrors('downloadTranscriptDateRange');
        }
    }, [watchedDateRange, clearErrors]);

    const prepareDataForCsv = (transcripts: TranscriptHistoricalRecord[]) => {
        const headerRow = `${t('classes.sent-at')}, ${t('classes.breakout-room')}, ${t('classes.from')}, ${t('classes.text')}\n`;

        const dataForCSV = transcripts.map((record) => {
            const { sentAt, from, text, breakoutRoom } = normalizeTranscriptRecord(record, t);
            return { sentAt, from, text, breakoutRoom };
        });

        const parsedData = unparse(dataForCSV, {
            header: false,
            columns: ['sentAt', 'breakoutRoom', 'from', 'text']
        });

        return `${headerRow}${parsedData}`;
    };

    const downloadCsv = (transcripts: TranscriptHistoricalRecord[]) => {
        const data = prepareDataForCsv(transcripts);
        const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), data], { type: 'text/csv;charset=utf-8;' });

        createAndClickDownloadUrl(
            URL.createObjectURL(blob),
            t('classes.csv-transcripts-title', { classTitle: className })
        );
    };

    const handleClose = () => {
        onClose?.({}, 'backdropClick');
    };

    const onSubmit = async () => {
        const [startDate, endDate] = getValues('downloadTranscriptDateRange');

        const selectedStartTime = dayjs(startDate).startOf('day').unix();
        const selectedEndTime = dayjs(endDate).endOf('day').unix();

        const params: DownloadTranscriptOptions = {
            class_id: classId ?? 0,
            start_time: selectedStartTime,
            end_time: selectedEndTime,
            user_uuid: userUuid,
            class_uuid: classUuid
        };

        if (classUuid) {
            params.class_uuid = classUuid;
        }

        const transcripts = await getTranscripts(params);

        if (!transcripts) return;

        if (transcripts.length === 0) {
            setError('downloadTranscriptDateRange', { message: t('classes.no-transcripts') });

            return;
        }

        downloadCsv(transcripts);

        enqueueSnackbar(t('classes.transcript-download-confirmation'), { variant: 'success' });
        handleClose();
    };

    const isLoading = transcriptStatus === Status.InProgress;

    return (
        <StyledDialog onClose={handleClose} {...other}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>
                    <Typography variant="h5">
                        {t('classes.download-transcripts-for', {
                            classTitle: className,
                            interpolation: { escapeValue: false }
                        })}
                    </Typography>
                </DialogTitle>

                <DialogContent>{t('classes.download-transcripts-description')}</DialogContent>

                <DateRangeContainer>
                    <ControlledDateRange
                        name="downloadTranscriptDateRange"
                        startLabel={t('classes.start-date')}
                        endLabel={t('classes.end-date')}
                        control={control}
                        separator={t('to')}
                        calendars={1}
                        defaultValue={[dayjs().startOf('day').subtract(6, 'days'), dayjs().endOf('day')]}
                    />
                </DateRangeContainer>

                <DialogActions>
                    <Button onClick={handleClose} variant="outlined">
                        {t('cancel')}
                    </Button>

                    <StyledLoadingButton
                        id="transcripts-csv-download-btn"
                        disabled={!classId}
                        loading={isLoading}
                        variant="contained"
                        onClick={onSubmit}
                    >
                        {t('classes.export')}
                    </StyledLoadingButton>
                </DialogActions>
            </form>
        </StyledDialog>
    );
};
